import { DEFAULT_DEVICE_PREFERENCES, type DevicePreferences } from "@centvox/shared/media";
import { DEFAULT_EFFECTS, type EffectPreferences } from "../media/effects-policy.js";

/**
 * Cihaz tercihlerinin katılım ekranından oda ekranına taşınması.
 *
 * ### ⚠️ Neden `sessionStorage`?
 * Katılım `location.assign()` ile tam sayfa geçişi yapar (Faz 1'de router
 * kütüphanesi yoktur) → bellek içi durum kaybolur. `sessionStorage` sekme
 * kapanınca silinir; `localStorage` **kalıcıdır** ve paylaşılan bir bilgisayarda
 * bir sonraki kullanıcıya "kamera açık" tercihini miras bırakır.
 *
 * ### ⚠️ Burada token TUTULMAZ
 * Erişim jetonu **yalnız bellektedir** (`api/fetcher.ts`). Depolamaya yazmak
 * XSS'te jetonu okunabilir kılar; Rapor 03 §6'nın açık kuralıdır.
 *
 * ### ⚠️ `deviceId` **kalıcı bir kimlik değildir**
 * Tarayıcı izin sıfırlandığında veya farklı köken altında `deviceId` değişir.
 * Bu yüzden kayıtlı kimlik listede yoksa **sessizce varsayılana düşülür**;
 * `getUserMedia`'ya ölü bir `exact` kimlik göndermek `OverconstrainedError`
 * üretir ve kullanıcı hiçbir cihaza erişemez.
 */

const KEY = "centvox:devices";
const EFFECTS_KEY = "centvox:effects";

export function loadPreferences(): DevicePreferences {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw === null) return { ...DEFAULT_DEVICE_PREFERENCES };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return { ...DEFAULT_DEVICE_PREFERENCES };
    const p = parsed as Partial<Record<keyof DevicePreferences, unknown>>;
    return {
      audioDeviceId: typeof p.audioDeviceId === "string" ? p.audioDeviceId : null,
      videoDeviceId: typeof p.videoDeviceId === "string" ? p.videoDeviceId : null,
      mic: p.mic !== false,
      cam: p.cam !== false,
    };
  } catch {
    // ⚠️ Gizli sekmede veya depolama kapalıyken `sessionStorage` FIRLATIR.
    //    Tercih kaybı görüşmeyi engellememelidir.
    return { ...DEFAULT_DEVICE_PREFERENCES };
  }
}

export function savePreferences(prefs: DevicePreferences): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* depolama yok — tercih bu oturumda taşınmaz, akış devam eder */
  }
}

/** Kayıtlı cihaz artık yoksa `null`'a düşürür (ölü `exact` kimlik koruması). */
export function reconcileDevices(
  prefs: DevicePreferences,
  available: { audioIn: { deviceId: string }[]; videoIn: { deviceId: string }[] },
): DevicePreferences {
  const audioOk =
    prefs.audioDeviceId !== null &&
    available.audioIn.some((d) => d.deviceId === prefs.audioDeviceId);
  const videoOk =
    prefs.videoDeviceId !== null &&
    available.videoIn.some((d) => d.deviceId === prefs.videoDeviceId);
  return {
    ...prefs,
    audioDeviceId: audioOk ? prefs.audioDeviceId : null,
    videoDeviceId: videoOk ? prefs.videoDeviceId : null,
  };
}

/**
 * ⭐ **Efekt tercihleri** (F4-07/F4-08) — green room'dan odaya taşınır.
 *
 * ⚠️ Cihaz tercihleriyle **aynı depoda ama ayrı anahtarda**: efekt ayarı
 *    bozuk bir cihaz kaydı yüzünden kaybolmamalıdır (ve tersi).
 *
 * ⚠️ Bilinmeyen değer **varsayılana** düşer, hata fırlatmaz: eski bir sürümde
 *    kaydedilmiş bir seçenek, yeni sürümde odaya girişi engellememelidir.
 */
export function loadEffects(): EffectPreferences {
  try {
    const raw = sessionStorage.getItem(EFFECTS_KEY);
    if (raw === null) return { ...DEFAULT_EFFECTS };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return { ...DEFAULT_EFFECTS };
    const p = parsed as Record<string, unknown>;

    const blur = (["off", "light", "strong", "image"] as const).find((x) => x === p["blur"]);
    const noise = (["off", "browser", "rnnoise"] as const).find((x) => x === p["noise"]);
    return {
      blur: blur ?? DEFAULT_EFFECTS.blur,
      noise: noise ?? DEFAULT_EFFECTS.noise,
      backgroundImage: typeof p["backgroundImage"] === "string" ? p["backgroundImage"] : null,
    };
  } catch {
    return { ...DEFAULT_EFFECTS };
  }
}

export function saveEffects(prefs: EffectPreferences): void {
  try {
    sessionStorage.setItem(EFFECTS_KEY, JSON.stringify(prefs));
  } catch {
    /* depolama yok — tercih bu oturumda taşınmaz, akış devam eder */
  }
}
